import './assets/main.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import App from './App.vue'
import router from './router'
import { useAuthStore } from './stores/auth'
import { useTranslationsStore } from './stores/translations'
import { createI18nInstance, detectLocale } from './i18n/index.js'
import mitt from 'mitt'
import * as Sentry from '@sentry/vue'
import { bridge } from './services/bridge.js'
import analytics from '@services/analytics.ts'
import { toastPlugin } from './composables/useToast.js'

import GameService from '@services/game.js'
import './utils/memoryLeakDetector.js'
import { avatarFallback } from './directives/avatarFallback.js'

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

const app = createApp(App)

// WebView.setWebContentsDebuggingEnabled(BuildConfig.DEBUG)

const isProduction = import.meta.env.PROD
const environment = isProduction ? 'production' : 'development'
const gitCommit = import.meta.env.VITE_GIT_COMMIT
const appVersion = import.meta.env.VITE_APP_VERSION
const buildTime = import.meta.env.VITE_BUILD_TIME

Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment,
    release: gitCommit || `frontend@${appVersion}`,
    beforeSend(event, hint) {
        if (!isProduction) {
            console.log('Sentry event (dev):', event)
        }

        const error = hint.originalException || hint.syntheticException

        if (error) {
            if (
                error.name === 'ChunkLoadError' ||
                error.message?.includes('Failed to fetch dynamically imported')
            ) {
                return null
            }

            if (error.message?.includes('ResizeObserver loop limit exceeded')) {
                return null
            }

            if (error.message?.includes('Non-Error promise rejection captured')) {
                return null
            }
        }

        if (event.exception) {
            const errorMessage = event.exception.values?.[0]?.value || ''
            if (errorMessage.includes('NetworkError') || errorMessage.includes('Failed to fetch')) {
                if (!isProduction) {
                    return null
                }
            }
        }

        return event
    },
    ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        'Non-Error promise rejection captured',
        'ChunkLoadError',
    ],
    sampleRate: isProduction ? 1.0 : 1.0,
    initialScope: {
        tags: {
            ...(buildTime && { buildTime }),
        },
        contexts: {
            app: {
                ...(appVersion && { version: appVersion }),
                ...(gitCommit && { commit: gitCommit }),
            },
        },
    },
})

const i18n = createI18nInstance(detectLocale())

app.use(pinia)
app.use(i18n)
app.use(toastPlugin)

app.directive('avatar-fallback', avatarFallback)

const serviceBus = mitt()

app.config.globalProperties.$gameService = GameService
app.config.globalProperties.$serviceBus = serviceBus
app.config.globalProperties.$bridge = bridge
app.config.globalProperties.$analytics = analytics

// Expose serviceBus globally so it's accessible from Pinia stores
window.$serviceBus = serviceBus

window.addEventListener('bridge-dev-outbound', (e) => {
    console.log('DEV outbound envelope:', e.detail) // full envelope here
})

let isAppInitialized = false

// vue-router starts its initial navigation the moment it is installed, so it
// must only be installed once auth is settled - otherwise the guard sees
// token=null/user=null on a clean install, redirects to /login and nothing
// navigates back once the bridge token has been exchanged.
let routerInstalled = false

function installRouter() {
    if (routerInstalled) return
    routerInstalled = true
    app.use(router)
}

function mountApp() {
    installRouter()
    app.mount('#app')
}

function getOAuthToken() {
    if (window.AUTH_TOKEN) {
        console.log('Token found in window.AUTH_TOKEN')
        return window.AUTH_TOKEN
    }
    return null
}

function requestTokenFromBridge() {
    if (
        window.webkit &&
        window.webkit.messageHandlers &&
        window.webkit.messageHandlers.tokenBridge
    ) {
        console.log('Requesting token via native bridge...')
        window.webkit.messageHandlers.tokenBridge.postMessage('requestToken')
        return true
    }
    console.warn('Native bridge not available for token request.')
    return false
}

async function initializeApp(tokenValue) {
    if (isAppInitialized) return

    isAppInitialized = true

    // Register the mount failsafe before anything else. The boot sequence
    // below awaits network calls (translations, /api/v1/user) with no
    // client-side timeout of their own - if those hang, this is the only
    // thing standing between a slow backend and an app that never mounts.
    let appMounted = false
    const forceMountTimer = setTimeout(() => {
        if (!appMounted) {
            console.warn('Splash handoff timed out, force-mounting app')
            document.getElementById('loadingScreen')?.remove()
            mountApp()
            appMounted = true
        }
    }, 8000)

    analytics.init()

    const translationsStore = useTranslationsStore()
    translationsStore.init(i18n)
    // Follow the native app / browser locale unless the user has explicitly
    // chosen a language, in which case the persisted choice wins.
    if (!translationsStore.localeOverridden) {
        translationsStore.locale = detectLocale()
    }
    await translationsStore.loadTranslations()

    const authStore = useAuthStore()

    const hasValidStoredToken = await authStore.initializeFromStorage()

    if (hasValidStoredToken && authStore.user) {
        console.log('Using valid token from storage')
        Sentry.setUser({
            id: String(authStore.user.id),
            email: authStore.user.email,
            username: authStore.user.name || authStore.user.email,
        })
    } else if (tokenValue) {
        console.log(
            'Storage token invalid or missing, using bridge token:',
            tokenValue.substring(0, 10) + '...',
        )
        await authStore.setToken(tokenValue)
    } else {
        console.log('No valid stored token and no bridge token available - user needs to login')
    }

    // Auth is settled: start the initial navigation now (original hash route is
    // kept) and wait for it so the first paint is the right screen. If the
    // failsafe already mounted the app, the router is installed and this is a no-op.
    installRouter()
    await router.isReady()

    const skipSplash = import.meta.env.VITE_SKIP_SPLASH_SCREEN === 'true'

    if (skipSplash) {
        clearTimeout(forceMountTimer)
        if (!appMounted) {
            const loadingScreen = document.getElementById('loadingScreen')
            if (loadingScreen) {
                loadingScreen.remove()
            }
            mountApp()
            appMounted = true
        }
        return
    }

    if (window.notifyVueReady) {
        window.notifyVueReady()
    }

    window.addEventListener(
        'loading-handoff-ready',
        () => {
            if (appMounted) return
            clearTimeout(forceMountTimer)

            const loadingScreen = document.getElementById('loadingScreen')
            if (loadingScreen) {
                loadingScreen.classList.add('fade-out')
                setTimeout(() => {
                    if (loadingScreen.parentNode) {
                        loadingScreen.remove()
                    }
                    if (!appMounted) {
                        mountApp()
                        appMounted = true
                    }
                }, 500)
            } else {
                if (!appMounted) {
                    mountApp()
                    appMounted = true
                }
            }
        },
        { once: true },
    )
}

const initialToken = getOAuthToken()

if (initialToken) {
    initializeApp(initialToken)
} else {
    const bridgeAvailable = requestTokenFromBridge()

    if (bridgeAvailable) {
        console.log('Native bridge available, waiting for token...')

        document.addEventListener(
            'auth-token-ready',
            async (event) => {
                console.log('Token received via event')
                if (event.detail && event.detail.token) {
                    await initializeApp(event.detail.token)
                } else {
                    console.error('Received auth event but no token found')
                    await initializeApp(null)
                }
            },
            { once: true },
        )

        setTimeout(async () => {
            if (!isAppInitialized) {
                console.warn('Timeout waiting for token, starting app without authentication')
                await initializeApp(null)
            }
        }, 5000)
    } else {
        console.warn(
            'No native bridge or initial token available, starting app without authentication',
        )
        initializeApp(null)
    }
}
